import React, { useEffect, useMemo, useState } from 'react';
import './EvaluatedMeterDetails.less';
import { useSelector } from 'react-redux';
import { jobsSelectors } from '../../../../redux/jobs';
import BasicScreen from '../../../common/BasicScreen/BasicScreen';
import Toolbar from '../../../../components/envago/Toolbar/Toolbar';
import { useTranslation } from 'react-i18next';
import Card from '../../../../components/envago/Card/Card';
import LabeledList from '../../../../components/envago/LabeledList/LabeledList';
import OptionalLabel from '../../../../components/envago/OptionalLabel/OptionalLabel';
import MeterMeta from '../../../../components/envago/MeterMeta';
import MeterReadingEventsHistory from '../MeterReadingEventsHistory';
import { resolveReadableRateNameWithObis } from '../../../../helper/MeterHelper';
import { splitJoin } from '../../../../helper/TextHelper';
import classNames from 'classnames';
import { ApplicationConfig } from '../../../../redux/application/application.types';
import { applicationSelectors } from '../../../../redux/application';
import { mergeJobConfig } from '../../../../helper/JobConfigHelper';
import { loginSelectors } from '../../../../redux/login';
import jobApi from '../../../../api/job.api';
import { MeterReadingPreviewImage } from '../../../JobsScreen/components/JobDetails/components/JobMeterRateTaskReadingImages';
import { Helmet } from 'react-helmet-async';
import { useFieldVisibility } from '../../../../fieldVisibility/useFieldVisibility';

interface IEvaluatedMeterDetailsProps {
    jobId: string;
}

const EvaluatedMeterDetails = ({ jobId }: IEvaluatedMeterDetailsProps) => {
    const { t } = useTranslation();

    const job = useSelector(jobsSelectors.getJobById(jobId));
    const login = useSelector(loginSelectors.getLoginById(job.login));

    const applicationName = useSelector(applicationSelectors.getApplicationName);
    const visibility = useFieldVisibility();
    const applicationConfig: ApplicationConfig = useSelector(applicationSelectors.getApplicationConfig);

    const [readingResultImages, setReadingResultImages] = useState<Array<MeterReadingPreviewImage>>([]);

    const emptyRates = useMemo(() => {
        return job.meter.meterRateTasks.reduce((acc, curr) => {
            if ((!curr.readingResults || curr.readingResults.length === 0) && (!curr.readingEvents || curr.readingEvents.length === 0)) {
                acc.push(resolveReadableRateNameWithObis(curr));
                return acc;
            }

            return acc;
        }, [] as string[]);
    }, [job]);

    useEffect(() => {
        if (!job || !job.done || !login?.token) {
            return;
        }

        const jobConfig = mergeJobConfig(job.config, applicationConfig);

        if (!jobConfig || !jobConfig.features?.evaluationShowReadingImagesAllowed) {
            return;
        }

        const abortController = new AbortController();

        const imageFetchPromises = job.meter.meterRateTasks.flatMap((mr) => {
            const lastReadingResult = mr.readingResults?.slice(-1)?.[0];

            if (lastReadingResult?.readingResultImages && lastReadingResult.readingResultImages.length > 0) {
                return lastReadingResult.readingResultImages.map((i) => {
                    const promise = jobApi.fetchReadingImagesForMeterRateTask(job.id, mr.taskId, i.imageId, login.token, abortController).then((res) => ({
                        taskId: mr.taskId,
                        imageId: i.imageId,
                        base64: res,
                    }));

                    return promise;
                });
            }
            return [];
        });

        Promise.all(imageFetchPromises)
            .then((results) => {
                setReadingResultImages(
                    results.map((r, index) => ({
                        ...r,
                        index: index,
                    })),
                );
            })
            .catch((error) => {});

        return () => {
            abortController.abort();
        };
    }, [job?.meter.meterRateTasks, login?.token, applicationConfig]);

    if (!job) return null;

    return (
        <BasicScreen key={job.id}>
            <Helmet>
                {/* Der BROWSER-TAB-Titel, keine Kopfzeile im DOM. splitJoin entfernt
                    die Leerstellen der versteckten Teile; sind beide weg, bleibt
                    „Auswertung" stehen — applicationName und das Wort tragen den
                    Kontext, ein Rueckfall ist nicht noetig. */}
                <title>
                    {applicationName} | Auswertung{' '}
                    {splitJoin(
                        ' ',
                        visibility.pick(
                            'meter.meterType',
                            job.meter.customMeterType || t(`common.metertypes.${job.meter.meterType}`),
                        ),
                        visibility.pick('meter.meterNumber', job.meter.meterNumber),
                    )}
                </title>
            </Helmet>
            <Toolbar showBackButton={true} title={t('evaluation.details.title')} />
            <Card className={'evaluated-meter-details'}>
                <MeterMeta meter={job.meter} className={'mb-2 pb-2 border-b-2 border-gray-100 border-solid'} />
                <LabeledList className={'mb-2 pb-2 border-b-2 border-gray-100 border-solid'}>
                    <OptionalLabel label={t('evaluation.details.labels.history')} labelClassName={'self-start'}>
                        {job.meter.meterRateTasks.map((task, index) => (
                            <MeterReadingEventsHistory
                                key={index}
                                task={task}
                                defaultOpen={job.meter.meterRateTasks.length === 1}
                                readingResultImages={readingResultImages?.filter((i) => i.taskId === task.taskId)}
                            />
                        ))}
                        {emptyRates.length > 0 && (
                            <div
                                className={classNames('', {
                                    'text-gray-400 text-sm font-medium mt-2': job.meter.meterRateTasks.length > emptyRates.length,
                                })}
                            >
                                {emptyRates.length > 1
                                    ? t('evaluation.empty.noReadingEventsForMeterRates', {
                                          rateNames: splitJoin(', ', ...emptyRates),
                                      })
                                    : t('evaluation.empty.noReadingEventsForMeterRate', {
                                          rateName: splitJoin('', ...emptyRates),
                                      })}
                            </div>
                        )}
                    </OptionalLabel>
                </LabeledList>
            </Card>
        </BasicScreen>
    );
};

export default EvaluatedMeterDetails;
